import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Container,
  Typography,
  Alert,
  Skeleton,
  Stack,
} from "@mui/material";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { singIn, getUser } from "../../action/authActions";
import CompetitionBuyVotesContent from "../../component/users/competitions/CompetitionBuyVotesContent";

const TOKEN_ERRORS = {
  missing:
    "Authentication required. Open this page from the app with a valid login token.",
  invalid: "Invalid or expired token.",
  guest: "Please register in the app before purchasing votes.",
};

function validateToken(rawToken) {
  if (!rawToken || !String(rawToken).trim()) {
    return { ok: false, error: TOKEN_ERRORS.missing };
  }
  try {
    const user = jwtDecode(rawToken);
    if (user?.device_id) {
      return { ok: false, error: TOKEN_ERRORS.guest };
    }
    if (!user?.id) {
      return { ok: false, error: TOKEN_ERRORS.invalid };
    }
    return { ok: true, token: rawToken, user };
  } catch {
    return { ok: false, error: TOKEN_ERRORS.invalid };
  }
}

function PageShell({ children }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#0f1118",
        backgroundImage:
          "radial-gradient(ellipse 80% 50% at 50% -20%, rgba(255,205,31,0.08), transparent), radial-gradient(ellipse 60% 40% at 100% 100%, rgba(239,54,78,0.06), transparent)",
        py: { xs: 4, md: 8 },
        px: 2,
      }}
    >
      <Container maxWidth="lg">{children}</Container>
    </Box>
  );
}

function PricingSkeleton() {
  return (
    <Stack spacing={3} alignItems="center">
      <Skeleton
        variant="rounded"
        width={320}
        height={48}
        sx={{ bgcolor: "rgba(255,255,255,0.06)" }}
      />
      <Skeleton
        variant="rounded"
        width={480}
        height={24}
        sx={{ bgcolor: "rgba(255,255,255,0.06)" }}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 2.5,
          width: "100%",
          mt: 2,
        }}
      >
        {[0, 1, 2].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={420}
            sx={{ bgcolor: "rgba(255,255,255,0.06)", borderRadius: 3 }}
          />
        ))}
      </Box>
    </Stack>
  );
}

export default function CompetitionBuyVotesPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const urlToken = searchParams.get("token");
  const tokenCheck = useMemo(() => validateToken(urlToken), [urlToken]);

  const [loading, setLoading] = useState(Boolean(tokenCheck.ok));
  const [loadError, setLoadError] = useState(null);
  const [competition, setCompetition] = useState(null);

  useEffect(() => {
    if (!tokenCheck.ok) return;

    const { token, user } = tokenCheck;
    localStorage.setItem("mynetgoals.com-token", token);
    dispatch(singIn(token));
    dispatch({ type: "SAVE_LOGGED_USER", payload: user });
    localStorage.removeItem("mynetgoals.com-userData");
    dispatch(getUser(token, user.id));
  }, [tokenCheck, dispatch]);

  useEffect(() => {
    if (!tokenCheck.ok || !id) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL_USER}/getCompetitionDetail/${id}`,
          {
            headers: {
              Authorization: `Bearer ${tokenCheck.token}`,
            },
          }
        );
        if (cancelled) return;
        if (res.data?.success) {
          setCompetition(res.data.data);
        } else {
          setLoadError(res.data?.message || "Could not load competition.");
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(
            e?.response?.data?.message || "Could not load competition."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [tokenCheck, id]);

  if (!tokenCheck.ok) {
    return (
      <PageShell>
        <Box
          sx={{
            maxWidth: 480,
            mx: "auto",
            mt: 6,
            p: 3,
            borderRadius: 3,
            bgcolor: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Alert severity="error">{tokenCheck.error}</Alert>
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 }, maxWidth: 640, mx: "auto" }}>
        <Typography
          component="p"
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "#FFCD1F",
            mb: 1.5,
          }}
        >
          Vote packs
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 800,
            fontSize: { xs: 32, sm: 40, md: 48 },
            color: "#fff",
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            mb: 2,
          }}
        >
          Plans that grow with you
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.58)",
            fontSize: { xs: 15, md: 17 },
            lineHeight: 1.6,
          }}
        >
          {competition?.name
            ? `Choose a vote pack for ${competition.name}. Votes are added to your wallet instantly and work across the app.`
            : "Choose a vote pack. Votes are added to your wallet instantly and work across the app."}
        </Typography>
      </Box>

      {loading ? (
        <PricingSkeleton />
      ) : loadError ? (
        <Box sx={{ maxWidth: 480, mx: "auto" }}>
          <Alert severity="error">{loadError}</Alert>
        </Box>
      ) : (
        <CompetitionBuyVotesContent
          competitionId={id}
          competitionName={competition?.name}
          votePurchase={competition?.vote_purchase}
          token={tokenCheck.token}
          remainingVotes={competition?.remainingVotes}
          showIntro={false}
          variant="page"
        />
      )}
    </PageShell>
  );
}
